import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { Help } from "../components/Help";
import { useApp } from "../context";
import type { SessionUser, TrustedDevice } from "../data";
import { forgetDevice } from "../mfaTrust";
import { BackupCodes, MfaSetup, StepUpForm } from "./Mfa";

type StepUpAction = "newCodes" | "turnOff";

/** Settings: the user's own two-step sign in, the remembered devices, and (owner) whether managers must use it. */
export function MfaSettings({ user, isOwner }: { user: SessionUser; isOwner: boolean }) {
  const { t, i18n } = useTranslation();
  const { store, workshop, reloadWorkshop, mfa, reloadMfa } = useApp();
  const [mode, setMode] = useState<"view" | "setup" | "codes">("view");
  const [codes, setCodes] = useState<string[]>([]);
  const [left, setLeft] = useState<number | null>(null);
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  // on a remembered device the session has not passed the code: new backup codes and switching off ask it first
  const [stepUp, setStepUp] = useState<StepUpAction | null>(null);
  const [error, setError] = useState<unknown>(null);
  const on = mfa?.status.factor ?? false;
  const verified = mfa?.status.verified ?? false;
  const required = mfa?.requirement.required ?? false;
  const managers = workshop.general.mfaManagers === true;
  const date = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));

  const loadDevices = useCallback(() => {
    if (on) void store.mfaDevices().then(setDevices, () => setDevices([]));
    else setDevices([]);
  }, [store, on]);
  useEffect(() => {
    if (on) void store.mfaBackupCodesLeft().then(setLeft, () => setLeft(null));
    loadDevices();
  }, [store, on, mode, loadDevices]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err);
    }
  };
  const done = async () => {
    setMode("view");
    await reloadMfa?.();
  };
  const newCodes = async () => {
    setCodes(await store.mfaNewBackupCodes());
    setMode("codes");
  };
  const turnOff = async () => {
    await store.mfaDisable();
    forgetDevice(user.id);
    await reloadMfa?.();
  };
  const act = (action: StepUpAction) => {
    if (action === "turnOff" && !globalThis.confirm?.(t("mfa.settings.confirmOff"))) return;
    if (!verified) {
      setError(null);
      setStepUp(action);
      return;
    }
    void run(action === "newCodes" ? newCodes : turnOff);
  };
  return (
    <section className="mfa-settings" aria-labelledby="mfa-settings-title">
      <h2 id="mfa-settings-title">{t("mfa.settings.title")}</h2>
      {mfa?.requirement.testExempt && !on && <p className="note mfa-test-note">{t("mfa.settings.testNote")}</p>}
      {mode === "setup" ? (
        <MfaSetup store={store} userId={user.id} email={user.email} reason={mfa?.requirement.reason ?? null} inline onDone={() => void done()} onSkip={() => setMode("view")} />
      ) : mode === "codes" ? (
        <BackupCodes codes={codes} email={user.email} onDone={() => void done()} />
      ) : (
        <>
          <p className={on ? "ok" : "muted"}>{t(on ? "mfa.settings.on" : "mfa.settings.off")}</p>
          {on && left !== null && <p className={left <= 2 ? "warn-text" : "muted"}>{t("mfa.settings.codesLeft", { count: left })}</p>}
          {on && required && <p className="muted">{t("mfa.settings.required")}</p>}
          {stepUp ? (
            <StepUpForm
              store={store}
              userId={user.id}
              intro={t("mfa.stepUp.intro")}
              onCancel={() => setStepUp(null)}
              onDone={() => {
                const action = stepUp;
                setStepUp(null);
                void run(async () => {
                  if (action === "newCodes") await newCodes();
                  else await turnOff();
                  await reloadMfa?.();
                });
              }}
            />
          ) : (
            <div className="actions">
              {!on && (
                <button type="button" className="primary" onClick={() => setMode("setup")}>
                  {t("mfa.settings.turnOn")}
                </button>
              )}
              {on && (
                <button type="button" onClick={() => act("newCodes")}>
                  {t("mfa.settings.newCodes")}
                </button>
              )}
              {on && !required && (
                <button type="button" className="danger" onClick={() => act("turnOff")}>
                  {t("mfa.settings.turnOff")}
                </button>
              )}
            </div>
          )}
          {on && (
            <div className="mfa-devices" role="group" aria-labelledby="mfa-devices-title">
              <h3 id="mfa-devices-title">
                {t("mfa.devices.title")} <Help id="mfa.devices" />
              </h3>
              <p className="muted">{t("mfa.devices.intro")}</p>
              {devices.length === 0 ? (
                <p className="muted">{t("mfa.devices.none")}</p>
              ) : (
                <ul className="mfa-device-list">
                  {devices.map((d) => (
                    <li key={d.id}>
                      <span className="mfa-device-name">
                        <strong>{d.label || t("mfa.devices.unknown")}</strong>
                        {d.current && <span className="tag">{t("mfa.devices.current")}</span>}
                      </span>
                      <small className="muted">
                        {t("mfa.devices.lastUsed", { date: date(d.lastUsedAt) })} · {t("mfa.devices.expires", { date: date(d.expiresAt) })}
                      </small>
                      <button type="button" onClick={() => void run(async () => { await store.mfaForgetDevice(d); loadDevices(); await reloadMfa?.(); })}>
                        {t("mfa.devices.forget")}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="actions">
                {devices.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!globalThis.confirm?.(t("mfa.devices.confirmAll"))) return;
                      void run(async () => {
                        await store.mfaForgetAllDevices();
                        loadDevices();
                        await reloadMfa?.();
                      });
                    }}
                  >
                    {t("mfa.devices.forgetAll")}
                  </button>
                )}
                {devices.some((d) => d.current) && (
                  <button type="button" onClick={() =>
                      void run(() => {
                        // like every sign out, the page address is forgotten too
                        globalThis.history?.replaceState?.(null, "", globalThis.location?.pathname ?? "/");
                        return store.signOut({ forgetDevice: true });
                      })
                    }>
                    {t("mfa.devices.signOutForget")}
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
      <ErrorText error={error} />
      <label className="field switch-field">
        <span className="field-label">{t("setting.mfaManagers.label")}</span>
        <span className="switch-row">
          <input
            type="checkbox"
            checked={managers}
            disabled={!isOwner}
            aria-label={t("setting.mfaManagers.label")}
            onChange={(e) => {
              const value = e.target.checked;
              void run(async () => {
                await store.saveGeneralSettings(workshop.id, { ...workshop.general, mfaManagers: value });
                await reloadWorkshop();
              });
            }}
          />
          <span>{managers ? t("manual.default.on") : t("manual.default.off")}</span>
        </span>
        <small>{t("setting.mfaManagers.description")}</small>
      </label>
    </section>
  );
}
